import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import DashboardListViewData from './dashboardListViewData';
import React, { useRef, useEffect, useState } from 'react';
import { FixedSizeList } from "react-window";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
  name: 250,
  purchase_price: 250,
  total_expenses: 250,
};

const ListView = React.memo(({ showResponsiveFilter, scrollRef, sites, sortBy, setSortBy, setOffset, filteredSitesCount, setSitesFetched }) => {

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const [siteDashboardColumnWidth, setSiteDashboardColumnWidth] = useState(() => {
    const storedWidths = JSON.parse(localStorage.getItem('siteDashboardColumnWidth')) || {};
    return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
  });

  useEffect(() => {
    localStorage.setItem('siteDashboardColumnWidth', JSON.stringify(siteDashboardColumnWidth));
  }, [siteDashboardColumnWidth]);

  const handleColumnWidthChange = (value, columnKey) => {
    setSiteDashboardColumnWidth({ ...siteDashboardColumnWidth, [columnKey]: value })
  }

  // Update window height on resize
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowHeight(window.innerHeight);
    }, 300); // 300ms debounce delay

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel(); // Cancel any pending debounced calls
    };
  }, []);


  const tableHeadRef = useRef(null);

  // synchronize horizontal scroll between table head and list
  useSyncHorizontalScroll(tableHeadRef, scrollRef);

  // Calculate fetched items on mount
  useCalculateFetchedItems(filteredSitesCount, setSitesFetched, 45, showResponsiveFilter ? 610 : 520);

  // Handle scroll and fetch more items
  const handleScrollList = useScrollFetchData(setOffset, setSitesFetched, filteredSitesCount, 45, showResponsiveFilter ? 610 : 520);

  const handleSort = (field) => {
    setSortBy((prevSortBy) => {
      const nextSortOrder = prevSortBy[field] === null ? 'asc'
        : prevSortBy[field] === 'asc' ? 'desc'
          : null;

      return Object.fromEntries(
        Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
      );
    });

  };

  const Row = ({ index, style, data }) => {
    const { sites } = data;
    const site = index === 0 ? sites[0] : sites[index];
    return (
      <Box key={index} style={{ ...style, width: 'fit-content' }}>
        <DashboardListViewData
          site={site}
          columnWidth={siteDashboardColumnWidth}
        />
      </Box>
    );
  };

  return (
    <Box className='table'>
      <Box className='tableHead' ref={tableHeadRef}>
        <CustomHeaderCell
          className='table_header_cell_non_filter'
          label='Site'
          columnKey='name'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={true}
          columnWidth={siteDashboardColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
        className='table_header_cell_non_filter'
          label="Prix d'achat"
          columnKey='purchase_price'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={true}
          fixSide='right'
          fixSidePosition={siteDashboardColumnWidth.total_expenses}
          columnWidth={siteDashboardColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          className='table_header_cell_non_filter'
          label='Dépense'
          columnKey='total_expenses'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={true}
          fixSide='right'
          columnWidth={siteDashboardColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
      </Box>

      <Box className='tableBody'>
        <FixedSizeList
          ref={scrollRef}
          height={Math.min(
            filteredSitesCount * 45 + 15,
            showResponsiveFilter ? windowHeight - 610 : windowHeight - 520
          )}
          width="100%"
          itemCount={filteredSitesCount}
          itemSize={45}
          itemData={{ sites }}
          onScroll={handleScrollList}
        >
          {Row}
        </FixedSizeList>
      </Box>
    </Box>

  );
});

export default ListView;